/**
 * What each plan includes. Mirrors server/pb_hooks/lib/plans.js, which enforces it (plans.test.ts
 * proves both agree); the client uses it to show limits, badges and upgrade prompts before the
 * server would refuse. A workspace has its owner's plan (`workspaces.plan`, empty = free).
 */
export type PlanId = "free" | "starter" | "pro";
export const PLAN_IDS: PlanId[] = ["free", "starter", "pro"];

/** Something a plan may not include; the upgrade dialog has copy for each. */
export type PlanFeature =
  | "templates"
  | "invoices"
  | "quotes"
  | "customers"
  | "paymentMethods"
  | "charts"
  | "workspaces"
  | "recurring"
  | "reminders"
  | "autoReminders"
  | "assistant"
  | "apps";

export type PlanLimits = {
  /** Template ids the plan may use; null = every template. */
  templates: readonly string[] | null;
  watermark: boolean;
  /** null = unlimited */
  invoicesPerMonth: number | null;
  quotesPerMonth: number | null;
  customers: number | null;
  paymentMethods: number | null;
  charts: boolean;
  workspaces: boolean;
  /** Recurring invoices. */
  recurring: boolean;
  /** Sending payment reminders by hand, and on the workspace's schedule. */
  reminders: boolean;
  autoReminders: boolean;
  /** Questions to the assistant per day (services/ai/src/lib/plans.ts enforces it). */
  assistantPerDay: number;
  /** Blanqo in Claude and ChatGPT (services/mcp). */
  apps: boolean;
};

/** Free has three designs; every paid plan has all of them. */
export const FREE_TEMPLATES = ["montana", "zurich", "sprite"] as const;

export const PLAN_LIMITS: Record<PlanId, PlanLimits> = {
  free: {
    templates: FREE_TEMPLATES,
    watermark: true,
    invoicesPerMonth: 5,
    quotesPerMonth: 5,
    customers: 5,
    paymentMethods: 1,
    charts: false,
    workspaces: false,
    recurring: false,
    reminders: false,
    autoReminders: false,
    assistantPerDay: 20,
    apps: false,
  },
  starter: {
    templates: null,
    watermark: false,
    invoicesPerMonth: 15,
    quotesPerMonth: null,
    customers: 15,
    paymentMethods: null,
    charts: true,
    workspaces: false,
    recurring: false,
    reminders: true,
    autoReminders: false,
    assistantPerDay: 100,
    apps: false,
  },
  pro: {
    templates: null,
    watermark: false,
    invoicesPerMonth: null,
    quotesPerMonth: null,
    customers: null,
    paymentMethods: null,
    charts: true,
    workspaces: true,
    recurring: true,
    reminders: true,
    autoReminders: true,
    assistantPerDay: 300,
    apps: true,
  },
};

export const normalizePlan = (plan: unknown): PlanId =>
  plan === "starter" || plan === "pro" ? plan : "free";

export function templateAllowed(plan: PlanId, templateId: string): boolean {
  const allowed = PLAN_LIMITS[plan].templates;
  return allowed === null || allowed.includes(templateId);
}

/** The cheapest plan that unlocks a template: Free's three, else Starter (every paid plan). */
export function templatePlan(templateId: string): PlanId {
  return (FREE_TEMPLATES as readonly string[]).includes(templateId) ? "free" : "starter";
}

/** The cheapest plan that includes a feature (for "Upgrade to …" buttons). */
export function planFor(feature: PlanFeature, current: PlanId): PlanId {
  if (
    feature === "workspaces" ||
    feature === "recurring" ||
    feature === "autoReminders" ||
    feature === "apps"
  ) {
    return "pro";
  }
  if (
    feature === "templates" ||
    feature === "charts" ||
    feature === "paymentMethods" ||
    feature === "quotes" ||
    feature === "reminders"
  ) {
    return "starter";
  }
  return current === "free" ? "starter" : "pro";
}

/** A limit reached: `used` of `limit` (null = unlimited, never reached). */
export const atLimit = (used: number | undefined, limit: number | null) =>
  limit !== null && used !== undefined && used >= limit;
